import {
  CalendarDate,
  Time,
  parseAbsolute,
  toCalendarDateTime,
  toZoned,
} from '@internationalized/date';

import { timezone } from '@/utils/timezone.util';

export const toUpperDenomination = (amount: number): number => amount / 100;

/**
 * Extracts the YYYY-MM-DD date portion from an ISO 8601 date or date-time string.
 * E.g. "2026-03-01T09:30:00.000Z" → "2026-03-01", "2026-03-01" → "2026-03-01"
 */
export const toDateOnlyString = (isoString: string): string => isoString.substring(0, 10);

export const toLowerDenomination = (amount: number): number => Math.round(amount * 100);

/**
 * Converts a UTC ISO string to a YYYY-MM-DD calendar date in the given IANA timezone.
 * The time component is respected: midnight boundaries differ per timezone.
 * E.g. "2024-03-15T20:00:00Z" in Asia/Tbilisi (UTC+4) → "2024-03-16"
 */
export const toLocalDate = (isoString: string, timeZone: string = timezone.get()): string => {
  const { year, month, day } = parseAbsolute(isoString, timeZone);

  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

/**
 * Converts a UTC ISO string to a local YYYY-MM-DDTHH:mm string in the given IANA timezone.
 * E.g. "2024-03-15T20:00:00Z" in Asia/Tbilisi (UTC+4) → "2024-03-16T00:00"
 */
export const toLocalDateTime = (isoString: string, timeZone: string = timezone.get()): string => {
  const { year, month, day, hour, minute } = parseAbsolute(isoString, timeZone);

  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
};

/**
 * Converts a local YYYY-MM-DDTHH:mm string (in the given IANA timezone) to a UTC ISO 8601 string.
 * E.g. "2025-06-15T00:00" in UTC+4 → "2025-06-14T20:00:00.000Z"
 */
export const toUtcIsoString = (localDateTime: string, timeZone: string = timezone.get()): string => {
  const [ datePart, timePart ] = localDateTime.split('T');
  const [ year, month, day ] = datePart.split('-').map(Number);
  const [ hour, minute ] = (timePart ?? '00:00').split(':').map(Number);
  const zoned = toZoned(
    toCalendarDateTime(new CalendarDate(year, month, day), new Time(hour, minute)),
    timeZone,
  );

  return zoned.toAbsoluteString();
};
